import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Statistic,
  Table,
  Tag,
  Typography
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  ReloadOutlined,
  WalletOutlined
} from '@ant-design/icons';
import { compactParams, manualSupportAdminAPI } from '../services/api';

const { Text } = Typography;

const CHANNEL_OPTIONS = [
  { value: 'paypal', label: 'PayPal' },
  { value: 'visa', label: 'Visa' }
];

const CHANGE_TYPE_LABEL = {
  admin_transfer: '总台转入/扣回',
  grant: '给玩家代充',
  shared_wallet_migration: '历史迁移',
  recharge: '充值'
};

export default function BalanceManagement() {
  const [wallets, setWallets] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [transferForm] = Form.useForm();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingTransfer, setPendingTransfer] = useState(null);
  const [transferring, setTransferring] = useState(false);

  const fetchWallets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await manualSupportAdminAPI.getChannelWallets();
      setWallets(res?.data?.data?.wallets || []);
    } catch (e) {
      message.error(e?.message || '余额加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLogs = useCallback(async (days = 30) => {
    setLogsLoading(true);
    try {
      const res = await manualSupportAdminAPI.getChannelWalletLogs({ days });
      setLogs(res?.data?.data?.logs || []);
    } catch (e) {
      message.error(e?.message || '流水加载失败');
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWallets();
    fetchLogs(30);
  }, [fetchWallets, fetchLogs]);

  const shared = useMemo(
    () => wallets.find((w) => w.shared) || wallets[0] || {},
    [wallets]
  );

  const submitTransfer = useCallback(async () => {
    const values = await transferForm.validateFields();
    setPendingTransfer(values);
    setConfirmOpen(true);
  }, [transferForm]);

  const doTransfer = useCallback(async () => {
    if (!pendingTransfer) return;
    setTransferring(true);
    try {
      const res = await manualSupportAdminAPI.creditChannelWallet(pendingTransfer.channel, {
        requestId: `ADM${Date.now()}${Math.random().toString(36).slice(2, 8).toUpperCase()}`,
        amount: pendingTransfer.amount,
        remark: pendingTransfer.remark || (pendingTransfer.amount > 0 ? '总台转入客服余额' : '总台扣回客服余额')
      });
      message.success(res?.data?.message || (pendingTransfer.amount > 0 ? '转入成功' : '扣回成功'));
      setConfirmOpen(false);
      setPendingTransfer(null);
      transferForm.resetFields();
      await fetchWallets();
      await fetchLogs(30);
    } catch (e) {
      message.error(e?.message || '操作失败');
    } finally {
      setTransferring(false);
    }
  }, [pendingTransfer, transferForm, fetchWallets, fetchLogs]);

  const logColumns = useMemo(() => [
    { title: '时间', dataIndex: 'createdAt', width: 170, render: (v) => (v ? String(v).replace('T', ' ').slice(0, 19) : '-') },
    { title: '渠道', dataIndex: 'channelName', width: 90 },
    {
      title: '类型',
      dataIndex: 'changeType',
      width: 130,
      render: (v, r) => {
        const label = CHANGE_TYPE_LABEL[v] || v || '-';
        if (v === 'grant') return <Tag color="blue">{label}</Tag>;
        if (String(r.sourceType || '').startsWith('admin_transfer')) {
          return Number(r.changeAmount) > 0
            ? <Tag color="green">总台转入</Tag>
            : <Tag color="orange">总台扣回</Tag>;
        }
        return label;
      }
    },
    {
      title: '金额(元)',
      dataIndex: 'changeAmount',
      width: 110,
      render: (v) => (
        <span style={{ color: Number(v) > 0 ? '#52c41a' : Number(v) < 0 ? '#f5222d' : '#666', fontWeight: 600 }}>
          {Number(v) > 0 ? '+' : ''}{v}
        </span>
      )
    },
    { title: '变动前', dataIndex: 'balanceBefore', width: 90 },
    { title: '变动后', dataIndex: 'balanceAfter', width: 90 },
    { title: '关联玩家', width: 150, render: (_, r) => [r.accountName, r.characterName].filter(Boolean).join(' / ') || '-' },
    { title: '操作人', dataIndex: 'operatorName', width: 110, render: (v) => v || '-' },
    { title: '备注', dataIndex: 'remark', ellipsis: true }
  ], []);

  return (
    <Card
      title="客服余额管理（人工代充）"
      extra={
        <Button icon={<ReloadOutlined />} loading={loading} onClick={() => { fetchWallets(); fetchLogs(30); }}>
          刷新
        </Button>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="PayPal / Visa 两个客服渠道共用同一客服余额池（共享钱包）。给玩家人工代充时会从该余额中扣减。"
      />

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card size="small">
            <Statistic
              title="客服余额（共享钱包）"
              value={Number(shared.balance) || 0}
              precision={2}
              prefix={<WalletOutlined />}
              suffix="元"
              valueStyle={{ color: '#1890ff', fontSize: 26 }}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small">
            <Statistic
              title="累计转入（真实充值）"
              value={Number(shared.totalGranted) || 0}
              precision={2}
              suffix="元"
              valueStyle={{ color: '#52c41a', fontSize: 22 }}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small">
            <Statistic
              title="累计消耗（代充支出）"
              value={Number(shared.totalConsumed) || 0}
              precision={2}
              suffix="元"
              valueStyle={{ color: '#f5222d', fontSize: 22 }}
            />
          </Card>
        </Col>
      </Row>

      <Card size="small" title="客服余额转入 / 扣回" style={{ marginBottom: 16 }}>
        <Form form={transferForm} layout="inline" initialValues={{ channel: 'paypal' }}>
          <Form.Item name="channel" label="渠道" rules={[{ required: true }]}>
            <Select options={CHANNEL_OPTIONS} style={{ width: 120 }} />
          </Form.Item>
          <Form.Item name="amount" label="金额(元)" rules={[{ required: true, message: '请输入金额' }]}>
            <InputNumber
              placeholder="正数转入 / 负数扣回"
              style={{ width: 180 }}
              precision={2}
              min={undefined}
            />
          </Form.Item>
          <Form.Item name="remark" label="备注" style={{ minWidth: 220, flex: 1 }}>
            <Input placeholder="备注（可选）" allowClear />
          </Form.Item>
          <Form.Item>
            <Button type="primary" icon={<ArrowUpOutlined />} loading={transferring} onClick={submitTransfer}>
              执行
            </Button>
          </Form.Item>
        </Form>
        <Text type="secondary" style={{ fontSize: 12 }}>
          说明：正数 = 总台向客服余额转入额度；负数 = 从客服余额扣回。扣减不能超过当前余额。
        </Text>
      </Card>

      <Card size="small" title={`客服余额流水（近30天，共 ${logs.length} 条）`}>
        <Table
          rowKey={(r) => r.id}
          columns={logColumns}
          dataSource={logs}
          loading={logsLoading}
          size="small"
          pagination={{ pageSize: 15, showSizeChanger: false }}
          locale={{ emptyText: '暂无流水' }}
        />
      </Card>

      <Modal
        title="确认操作"
        open={confirmOpen}
        onOk={doTransfer}
        onCancel={() => { setConfirmOpen(false); setPendingTransfer(null); }}
        okText="确认执行"
        cancelText="取消"
        confirmLoading={transferring}
      >
        {pendingTransfer && (
          <Descriptions column={1} size="small">
            <Descriptions.Item label="渠道">
              {CHANNEL_OPTIONS.find((o) => o.value === pendingTransfer.channel)?.label || pendingTransfer.channel}
            </Descriptions.Item>
            <Descriptions.Item label="金额">
              <Text strong style={{ color: pendingTransfer.amount > 0 ? '#52c41a' : '#f5222d' }}>
                {pendingTransfer.amount > 0 ? '+' : ''}{pendingTransfer.amount} 元
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="备注">{pendingTransfer.remark || '-'}</Descriptions.Item>
          </Descriptions>
        )}
        <Alert type="warning" showIcon style={{ marginTop: 8 }} message="该操作将直接变更客服余额，请确认无误。" />
      </Modal>
    </Card>
  );
}
